import { Helmet } from "react-helmet-async";
import { useEffect } from "react";

const SEO = ({
  title = "Anjali Daswani - Singer • Songwriter • Storyteller",
  description = "Born in Texas, raised in Nigeria, shaped by London, and now chasing music dreams in New York City. An artist with a passport full of stories and a sound that blends pop, R&B, and folk-inspired melodies.",
  url = "https://anjalidmusic.com",
  keywords = "Anjali Daswani, singer songwriter, pop music, R&B, folk music, NYC artist, girl on the train, square 1, music artist",
  isMusicPage = false,
  robots = "index, follow",

  // Social media props
  ogType = "website",
  ogImage = "https://anjalidmusic.com/images/og-image.png",
  ogImageAlt = "Anjali Daswani - Singer • Songwriter • Storyteller",
  ogImageWidth = "1200",
  ogImageHeight = "630",

  twitterCard = "summary_large_image",
  twitterImage = "https://anjalidmusic.com/images/twitter-image.png",
  twitterImageAlt = "Anjali Daswani - Singer • Songwriter • Storyteller",

  // Optional props
  ogSiteName = "Anjali Daswani Music",

  // Music-specific schema props
  songTitle = null, // For individual song pages
  album = null, // Album name if applicable
  genre = ["Pop", "R&B", "Folk"], // Music genres
  recordLabel = null, // Record label if signed
  releaseDate = null, // Release date for songs/albums

  logSchema = false, // Set to true to log schema to console for validation
}) => {
  // Base website schema
  // Base website schema
  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "Anjali Daswani Music",
    url: "https://anjalidmusic.com",
    description: description,
    author: {
      "@type": "Person",
      "@id": "https://anjalidmusic.com/bio",
      name: "Anjali Daswani",
      givenName: "Anjali",
      familyName: "Daswani",
      description:
        "Singer, songwriter, and storyteller blending pop, R&B, and folk-inspired melodies",
      birthPlace: "Texas, USA",
      homeLocation: "New York City, USA",
      jobTitle: ["Singer", "Songwriter", "Musician"],
      // Remove this line: genre: genre,
      sameAs: [
        "https://www.instagram.com/anjyyx/",
        "https://www.tiktok.com/@anjali.daswani",
        "https://www.facebook.com/profile.php?id=100066200794414",
        "http://www.linkedin.com/in/anjali-daswani-735669200",
        "https://open.spotify.com/artist/7BXnSLPZGMGarG0QwEUno8",
        "https://music.apple.com/us/artist/anjali-daswani/1750771474",
        "https://www.youtube.com/@anjalidaswani",
      ],
    },
  };

  // Person schema for the artist
  const personSchema = {
    "@context": "https://schema.org",
    "@type": ["Person", "MusicGroup"],
    "@id": "https://anjalidmusic.com/bio",
    name: "Anjali Daswani",
    givenName: "Anjali",
    familyName: "Daswani",
    alternateName: ["Anjali D", "Anjali D Music"],
    description:
      "Singer, songwriter, and storyteller from New York City blending pop, R&B, and folk-inspired melodies",
    birthPlace: {
      "@type": "Place",
      name: "Texas, USA",
    },
    homeLocation: {
      "@type": "Place",
      name: "New York City, USA",
    },
    jobTitle: ["Singer", "Songwriter", "Musician", "Recording Artist"],
    // Remove the hasOccupation field entirely - it was causing the validation errors
    genre: genre,
    knowsAbout: [
      "Music Performance",
      "Songwriting",
      "Recording",
      "Pop Music",
      "R&B",
      "Folk Music",
    ],
    url: "https://anjalidmusic.com",
    mainEntityOfPage: "https://anjalidmusic.com/bio", // Add this to fix the warning
    image: {
      "@type": "ImageObject",
      url: ogImage,
      width: ogImageWidth,
      height: ogImageHeight,
    },
    sameAs: [
      "https://www.instagram.com/anjyyx/",
      "https://www.tiktok.com/@anjali.daswani",
      "https://www.facebook.com/profile.php?id=100066200794414",
      "http://www.linkedin.com/in/anjali-daswani-735669200",
      "https://open.spotify.com/artist/7BXnSLPZGMGarG0QwEUno8",
      "https://music.apple.com/us/artist/anjali-daswani/1750771474",
      "https://www.youtube.com/@anjalidaswani",
    ],
  };

  // Music recording schema for the Music page
  const musicSchema = isMusicPage
    ? {
        "@context": "https://schema.org",
        "@type": "MusicAlbum",
        name: "Anjali Daswani Music Collection",
        byArtist: {
          "@type": "Person",
          "@id": "https://anjalidmusic.com/bio",
          name: "Anjali Daswani",
        },
        genre: genre,
        url: url,
        description: description,
        image: {
          "@type": "ImageObject",
          url: ogImage,
          width: ogImageWidth,
          height: ogImageHeight,
        },
        track: [
          {
            "@type": "MusicRecording",
            name: "Girl on the Train",
            byArtist: {
              "@type": "Person",
              "@id": "https://anjalidmusic.com/bio",
              name: "Anjali Daswani",
            },
            genre: ["Pop", "R&B", "Folk"],
            datePublished: "2025",
          },
          {
            "@type": "MusicRecording",
            name: "Square 1",
            byArtist: {
              "@type": "Person",
              "@id": "https://anjalidmusic.com/bio",
              name: "Anjali Daswani",
            },
            genre: ["Pop", "R&B", "Folk"],
            datePublished: "2024",
          },
        ],
      }
    : null;

  // 🧪 SCHEMA LOGGER FUNCTION
  useEffect(() => {
    if (logSchema && process.env.NODE_ENV === "development") {
      console.log("\n" + "=".repeat(60));
      console.log("🎵 SCHEMA MARKUP FOR VALIDATION - ANJALI DASWANI MUSIC");
      console.log("=".repeat(60));

      console.log("\n🌐 WEBSITE SCHEMA:");
      console.log("Copy this JSON for schema validation:");
      console.log("\n" + JSON.stringify(websiteSchema, null, 2));

      console.log("\n👤 PERSON SCHEMA (Artist):");
      console.log(JSON.stringify(personSchema, null, 2));

      if (musicSchema) {
        console.log("\n🎶 MUSIC COLLECTION SCHEMA:");
        console.log(JSON.stringify(musicSchema, null, 2));
      }

      // Quick validation links
      console.log("\n🔗 VALIDATION LINKS:");
      console.log("• Schema.org Validator: https://validator.schema.org/");
      console.log(
        "• Google Rich Results: https://search.google.com/test/rich-results",
      );
      console.log("• JSON-LD Playground: https://json-ld.org/playground/");
      console.log(
        "• Google Structured Data Testing: https://developers.google.com/search/docs/appearance/structured-data",
      );

      console.log("\n" + "=".repeat(60));
      console.log("✅ Music schemas logged successfully!");
      console.log("=".repeat(60) + "\n");
    }
  }, [logSchema, websiteSchema, personSchema, musicSchema]);

  return (
    <Helmet>
      {/* Standard Meta Tags */}
      <title>{title}</title>
      <link rel="canonical" href={url} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="robots" content={robots} />

      {/* Music-specific meta tags */}
      {songTitle && <meta name="music:song" content={songTitle} />}
      {genre && <meta name="music:genre" content={genre.join(", ")} />}
      <meta name="music:musician" content="Anjali Daswani" />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:alt" content={ogImageAlt} />
      <meta property="og:image:width" content={ogImageWidth} />
      <meta property="og:image:height" content={ogImageHeight} />
      <meta property="og:site_name" content={ogSiteName} />

      {/* Music-specific Open Graph */}
      {songTitle && <meta property="music:song" content={songTitle} />}
      {songTitle && (
        <meta property="music:musician" content="https://anjalidmusic.com" />
      )}
      <meta property="music:creator" content="Anjali Daswani" />

      {/* Twitter */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={twitterImage} />
      <meta name="twitter:image:alt" content={twitterImageAlt} />

      {/* Schema.org JSON-LD */}
      <script type="application/ld+json">
        {JSON.stringify(websiteSchema)}
      </script>

      <script type="application/ld+json">{JSON.stringify(personSchema)}</script>

      {isMusicPage && musicSchema && (
        <script type="application/ld+json">
          {JSON.stringify(musicSchema)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
